import { useEffect, useState } from 'react';
import { Layout } from '@/components/Layout';
import { api } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { formatDate } from '@/lib/format';
import { useSEO } from '@/lib/useSEO';
import { RefreshCw, CheckCircle2, AlertTriangle, Clock, PlayCircle } from 'lucide-react';
import { toast } from 'sonner';

const STATUS_META = {
  success:       { label: 'Éxito',      icon: CheckCircle2, cls: 'bg-emerald-100 text-emerald-800 border-emerald-200' },
  error:         { label: 'Error',      icon: AlertTriangle, cls: 'bg-rose-100 text-rose-800 border-rose-200' },
  skipped_lease: { label: 'Lease ocupado', icon: Clock,        cls: 'bg-slate-100 text-slate-600 border-slate-200' },
};

export default function AdminPayoutsRuns() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);

  useSEO({
    title: 'Ejecuciones del sweeper | Admin',
    description: 'Historial de ejecuciones automáticas del sweeper de payouts.',
    canonical: '/admin/payouts/runs',
  });

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/admin/payouts/sweep/runs', { params: { limit: 50 } });
      setItems(data.items || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const runNow = async (force) => {
    setRunning(true);
    try {
      const { data } = await api.post('/admin/payouts/sweep', null, { params: { force } });
      toast.success(`Ejecutado · +${data.evaluate.scheduled} programados · +${data.dispatch.sent} enviados · +${data.retry.requeued} reintentados`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error');
    } finally {
      setRunning(false);
    }
  };

  return (
    <Layout>
      <div className="mx-auto max-w-6xl px-4 py-10 sm:px-6 lg:px-8">
        <div className="flex items-end justify-between flex-wrap gap-4">
          <div>
            <div className="text-xs font-bold uppercase tracking-[0.25em] text-orange-600">Admin · Finanzas</div>
            <h1 className="font-display text-3xl font-black tracking-tighter text-slate-900 mt-1">Ejecuciones del sweeper</h1>
            <p className="text-sm text-slate-500 mt-1">Historial cronológico · cada 15 minutos + ejecuciones manuales</p>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button data-testid="runs-refresh-btn" onClick={load} variant="outline" className="rounded-none">
              <RefreshCw className="mr-1 h-4 w-4" /> Refrescar
            </Button>
            <Button data-testid="runs-run-now-btn" onClick={() => runNow(false)} disabled={running}
              className="rounded-none bg-slate-900 hover:bg-slate-800 text-white">
              <PlayCircle className="mr-1 h-4 w-4" /> {running ? 'Corriendo…' : 'Ejecutar ahora'}
            </Button>
          </div>
        </div>

        <div className="mt-6 border border-slate-200 bg-white overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-left text-[10px] font-bold uppercase tracking-wider text-slate-500">
              <tr>
                <th className="px-3 py-2">Fecha / hora</th>
                <th className="px-3 py-2">Duración</th>
                <th className="px-3 py-2">Estado</th>
                <th className="px-3 py-2">Host</th>
                <th className="px-3 py-2 text-right">Programados</th>
                <th className="px-3 py-2 text-right">Reintentados</th>
                <th className="px-3 py-2 text-right">Enviados</th>
                <th className="px-3 py-2 text-right">Completados</th>
                <th className="px-3 py-2 text-right">Bloqueados</th>
                <th className="px-3 py-2 text-right">Alertas</th>
                <th className="px-3 py-2">Error</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100" data-testid="runs-table">
              {loading && (<tr><td colSpan={11} className="p-8 text-center text-slate-500">Cargando…</td></tr>)}
              {!loading && items.length === 0 && (<tr><td colSpan={11} className="p-8 text-center text-slate-500">El scheduler aún no ha corrido. Ejecuta uno manualmente para verlo aquí.</td></tr>)}
              {items.map((r) => {
                const meta = STATUS_META[r.status] || { label: r.status, cls: 'bg-slate-100', icon: Clock };
                const Icon = meta.icon;
                const res = r.result || {};
                const ev = res.evaluate || {}, di = res.dispatch || {}, po = res.poll || {}, al = res.alerts || {}, rt = res.retry || {};
                return (
                  <tr key={r.run_id} className="hover:bg-slate-50" data-testid={`runs-row-${r.run_id}`}>
                    <td className="px-3 py-2 text-xs">
                      <div>{formatDate(r.started_at)}</div>
                      <div className="text-slate-400 font-mono text-[10px]">{r.run_id.slice(0, 8)}</div>
                    </td>
                    <td className="px-3 py-2 font-mono text-xs">{r.duration_ms != null ? `${r.duration_ms}ms` : '—'}</td>
                    <td className="px-3 py-2"><Badge className={`rounded-none border ${meta.cls} inline-flex items-center gap-1`}><Icon className="h-3 w-3" /> {meta.label}</Badge></td>
                    <td className="px-3 py-2 text-[10px] font-mono text-slate-500 truncate max-w-[160px]">{r.host || '—'}</td>
                    <td className="px-3 py-2 text-right font-mono">{ev.scheduled ?? '—'}</td>
                    <td className="px-3 py-2 text-right font-mono">{rt.requeued ?? '—'}</td>
                    <td className="px-3 py-2 text-right font-mono">{di.sent ?? '—'}</td>
                    <td className="px-3 py-2 text-right font-mono">{po.completed ?? '—'}</td>
                    <td className="px-3 py-2 text-right font-mono">{ev.held ?? '—'}</td>
                    <td className="px-3 py-2 text-right font-mono">{al.alerted ?? '—'}</td>
                    <td className="px-3 py-2 text-xs text-rose-700 max-w-[240px] truncate" title={r.error || ''}>{r.error || '—'}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        <div className="mt-4 text-xs text-slate-500">
          <b>Nota:</b> El scheduler corre automáticamente cada 15 minutos vía APScheduler in-process.
          Las ejecuciones manuales desde el panel también se registran aquí.
        </div>
      </div>
    </Layout>
  );
}
